import type { Spine } from "@esotericsoftware/spine-pixi-v8";

/**
 * Crossfade table for the clips that may follow one another on ONE skeleton.
 *
 * Spine blends between animations via `AnimationStateData` mix durations — strictly PER SKELETON
 * INSTANCE. Two clips playing on two different instances cannot be mixed; that is a hard swap.
 */
export interface SpineClipMixSpec {
  /** Every clip that may follow another on one skeleton (e.g. ["in","idle","win","out"]). */
  clipNames: readonly string[];
  /** Blend length in seconds. <= 0 disables mixing entirely (hard cuts). */
  seconds: number;
  /** Ordered [from, to] pairs to leave as HARD CUTS (a beat that must land un-smeared). */
  skipPairs?: readonly (readonly [string, string])[];
}

/** Instances whose mix table is already installed — these are POOLED, so re-applying is waste. */
const alreadyMixed = new WeakSet<Spine>();

/**
 * Install `spec`'s crossfades on one skeleton instance: every ordered pair of clips the skeleton
 * ACTUALLY carries (minus `skipPairs`). Clip presence is checked against the data because
 * spine-core's `setMix` THROWS on an unknown animation name, and symbol skeletons differ — e.g.
 * this game's low ranks carry no `idle`, and chance/freespins carry no `out`. Idempotent per instance.
 */
export function applySpineClipMixes(spine: Spine, spec: SpineClipMixSpec | null | undefined): void {
  if (!spec || spec.seconds <= 0) return;
  if (alreadyMixed.has(spine)) return;

  const data = spine.skeleton.data;
  const present = spec.clipNames.filter((name) => !!data.findAnimation(name));
  if (present.length < 2) {
    alreadyMixed.add(spine);
    return;
  }

  for (const from of present) {
    for (const to of present) {
      if (from === to) continue;
      if (spec.skipPairs?.some((pair) => pair[0] === from && pair[1] === to)) continue;
      spine.state.data.setMix(from, to, spec.seconds);
    }
  }
  alreadyMixed.add(spine);
}
